import type {
  PersonaPresentation,
  ReviewExtract,
  ReviewQuestionNote,
  SessionTurn,
} from '@matter_extractor/shared';

/**
 * What the REVIEW page draws of the conversation, and how a selection becomes a
 * position (relecture.md §4.2, §4.3, §5.2) - pure, no DOM, tested on its own.
 *
 * ## One coordinate system
 *
 * An extract is `start`, `end` and `text` in UTF-16 code units of the `reply` of
 * its turn, with `text === reply.slice(start, end)` (the contract of
 * `session-review.ts`). A string index in JavaScript IS a UTF-16 index, and so
 * is the length of a DOM `Range.toString()`: the three agree without a
 * conversion, which is the reason the page measures a selection by the text
 * the browser holds and never by a count of its own. The one thing that has to
 * hold, and `review-selection.ts` tests, is that NOTHING but the reply is text
 * inside the element that carries it: the pastilles are `::before` content, and
 * every accessible name is an attribute.
 *
 * ## What is not here
 *
 * The page shows nothing of the machine (D4): no step, no time, no malformed
 * mark, no reread. A turn is a number, an interviewer's message and the
 * person's reply, and `reviewTurnsOf` is where that is decided.
 */

/* ------------------------------------------------------------------ the turns */

/** One exchange as the review reads it. */
export interface ReviewTurn {
  /** 1-BASED - the « Tour N » of the conversation and the `turn` of an extract. */
  readonly number: number;
  /** The interviewer's message, variables already resolved. */
  readonly message: string;
  /** The person's answer, or null on the farewell (« aucune réponse attendue »). */
  readonly reply: string | null;
}

/**
 * The turns of a session, in the order they were played, WITHOUT the reread on
 * entry: it is a turn without an exchange (an empty message, a null reply) and
 * the review shows nothing of the scribe (§4.2).
 *
 * The number is `index + 1` - the one `ConversationScreen` prints, which counts
 * the rereads too - so « tour 6 » names the same turn on the two screens.
 */
export function reviewTurnsOf(turns: readonly SessionTurn[]): ReviewTurn[] {
  return turns
    .filter((turn) => !turn.reread)
    .map((turn) => ({ number: turn.index + 1, message: turn.message, reply: turn.reply }))
    .sort((a, b) => a.number - b.number);
}

/* ---------------------------------------------------------------- the speakers */

/** What a persona with an anonymous presentation speaks under (H110). */
export const ANONYMOUS_SPEAKER = 'IA';
/** What a person without a known first name speaks under (§12.6). */
export const PERSON_SPEAKER = 'Personne';

/**
 * The first name of the interviewer, or `IA`: only a NAMED presentation shows
 * one, and a version nobody could read speaks as `IA` too - the closed answer,
 * no name is invented.
 */
export function interviewerName(presentation: PersonaPresentation | null): string {
  return presentation?.kind === 'named' ? presentation.firstName : ANONYMOUS_SPEAKER;
}

/**
 * The first name of the person, or null. A value that holds an `@` is an email
 * typed in a field marked as the person's name, and the page shows no email.
 */
export function personFirstName(value: string | null | undefined): string | null {
  const name = value?.trim() ?? '';
  return name === '' || name.includes('@') ? null : name;
}

/* ------------------------------------------------------------------ the cover */

/** A span of a text that something covers: an extract, an annotated passage. */
export interface CoverSpan {
  readonly id: string;
  readonly start: number;
  readonly end: number;
}

/** A piece of the text, with the ids of every span that covers all of it. */
export interface CoverPiece {
  readonly start: number;
  readonly end: number;
  readonly ids: readonly string[];
}

/**
 * Cuts a text of `length` characters at every boundary of every span, and says
 * for each piece which spans cover it - none for the text between them.
 *
 * Two spans that overlap give three pieces, the middle one covered by both;
 * two that merely touch (`a.end === b.start`) stay apart, which is what makes
 * two extracts that follow each other TWO blocks (§4.3). A span outside the
 * text, or empty, covers nothing.
 */
export function coverPieces(length: number, spans: readonly CoverSpan[]): CoverPiece[] {
  const usable = spans.filter(
    (span) => span.start < span.end && span.start >= 0 && span.start < length,
  );
  const cuts = new Set<number>([0, length]);
  for (const span of usable) {
    cuts.add(span.start);
    if (span.end < length) cuts.add(span.end);
  }
  const points = [...cuts].sort((a, b) => a - b);
  const pieces: CoverPiece[] = [];
  for (let at = 0; at < points.length - 1; at++) {
    const start = points[at] ?? 0;
    const end = points[at + 1] ?? length;
    if (start >= end) continue;
    pieces.push({
      start,
      end,
      ids: usable.filter((span) => span.start <= start && span.end >= end).map((span) => span.id),
    });
  }
  return pieces;
}

/* ------------------------------------------------------------------ the reply */

/** A stretch of a text that nothing covers. */
export interface PlainPiece {
  readonly kind: 'plain';
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

/**
 * A stretch of a reply that one or more extracts cover - one MARK of §4.3.
 *
 * - `label` is the pastille: the numbers of the cases whose extract STARTS here,
 *   in the order of the grid, with a `?` after one that is in doubt - « 1, 6 »,
 *   « 2? ». Null on a stretch where none starts (the second half of an overlap).
 * - `extractIds` are the extracts that cover it, which is what a click opens.
 */
export interface MarkPiece {
  readonly kind: 'mark';
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly extractIds: readonly string[];
  readonly label: string | null;
  readonly doubt: boolean;
  readonly noted: boolean;
}

export type ReplyPiece = PlainPiece | MarkPiece;

/** The number of a case in the grid (1-based), or 0 for an objective the grid does not hold. */
export function caseNumber(order: readonly string[], objectiveId: string): number {
  return order.indexOf(objectiveId) + 1;
}

/**
 * The reply cut into plain text and marks.
 *
 * An extract whose `text` is not `reply.slice(start, end)` cannot be placed - a
 * reply never moves, so that is a row the server would not have written - and
 * is left out rather than drawn over the wrong words.
 */
export function replyPieces(
  reply: string,
  extracts: readonly ReviewExtract[],
  order: readonly string[],
): ReplyPiece[] {
  const placed = extracts.filter(
    (extract) =>
      extract.end <= reply.length &&
      extract.start < extract.end &&
      reply.slice(extract.start, extract.end) === extract.text,
  );
  const byId = new Map(placed.map((extract) => [extract.id, extract]));
  return coverPieces(reply.length, placed).map<ReplyPiece>((piece) => {
    const text = reply.slice(piece.start, piece.end);
    const covering = piece.ids.flatMap((id) => byId.get(id) ?? []);
    if (covering.length === 0) return { kind: 'plain', start: piece.start, end: piece.end, text };
    const starting = [
      ...new Set(
        covering
          .filter((extract) => extract.start === piece.start)
          .map((extract) => caseNumber(order, extract.objectiveId)),
      ),
    ].sort((a, b) => a - b);
    const label = starting
      .map((number) => {
        const doubtful = covering.some(
          (extract) => caseNumber(order, extract.objectiveId) === number && extract.doubt,
        );
        return `${number === 0 ? '·' : number}${doubtful ? '?' : ''}`;
      })
      .join(', ');
    return {
      kind: 'mark',
      start: piece.start,
      end: piece.end,
      text,
      extractIds: covering.map((extract) => extract.id),
      label: label === '' ? null : label,
      doubt: covering.some((extract) => extract.doubt),
      noted: covering.some((extract) => extract.note !== ''),
    };
  });
}

/* ---------------------------------------------------------------- the question */

/** A passage of an interviewer's message that an annotation bears on. */
export interface NotedPiece {
  readonly kind: 'noted';
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly noteIds: readonly string[];
  /** « Ne va pas » wins where a passage carries both verdicts. */
  readonly verdict: 'good' | 'bad';
}

export type QuestionPiece = PlainPiece | NotedPiece;

/**
 * The interviewer's message cut into plain text and annotated passages (§7).
 *
 * An annotation of the WHOLE message has no passage and marks nothing in the
 * text: its card is under the message. One whose passage no longer reads the
 * same words is left out, for the reason `replyPieces` gives.
 */
export function questionPieces(
  message: string,
  notes: readonly ReviewQuestionNote[],
): QuestionPiece[] {
  const placed = notes.filter(
    (note) =>
      note.passage !== null &&
      note.passage.end <= message.length &&
      message.slice(note.passage.start, note.passage.end) === note.passage.text,
  );
  const byId = new Map(placed.map((note) => [note.id, note]));
  const spans = placed.flatMap<CoverSpan>((note) =>
    note.passage === null
      ? []
      : [{ id: note.id, start: note.passage.start, end: note.passage.end }],
  );
  return coverPieces(message.length, spans).map<QuestionPiece>((piece) => {
    const text = message.slice(piece.start, piece.end);
    const covering = piece.ids.flatMap((id) => byId.get(id) ?? []);
    if (covering.length === 0) return { kind: 'plain', start: piece.start, end: piece.end, text };
    return {
      kind: 'noted',
      start: piece.start,
      end: piece.end,
      text,
      noteIds: covering.map((note) => note.id),
      verdict: covering.some((note) => note.verdict === 'bad') ? 'bad' : 'good',
    };
  });
}

/* ---------------------------------------------------------------- the selection */

/** A passage of a message, as a selection yields it. */
export interface Passage {
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

/**
 * The selection trimmed of the spaces round it, with its positions moved to
 * match - or null when only spaces were selected (§16, « ignorée »).
 *
 * `start` and `end` are raw offsets in `source`; the answer always satisfies
 * `text === source.slice(start, end)`, which is what the server checks word
 * for word.
 */
export function trimPassage(source: string, start: number, end: number): Passage | null {
  let from = Math.max(0, Math.min(start, source.length));
  let to = Math.max(from, Math.min(end, source.length));
  while (from < to && /\s/.test(source.charAt(from))) from++;
  while (to > from && /\s/.test(source.charAt(to - 1))) to--;
  if (from >= to) return null;
  return { start: from, end: to, text: source.slice(from, to) };
}

/** The sentence a selection across two messages is refused with (§5.2). */
export const SELECTION_REFUSAL =
  'Sélectionnez un passage dans un seul message : chaque morceau se range dans son tour.';

/** Shortens a passage for a title. */
export function shortPassage(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}
